<script lang="ts" setup>
import type { RegisterOrderExcelInfo } from '@/coreDomainsModule/entities/InputOrderExcelInfo';
import { i18n } from '@/i18n';

const t = i18n.t;

const props = defineProps<{
  orderExcelInfos: RegisterOrderExcelInfo[];
  selectedId: string;
}>();

const emits = defineEmits<{
  (e: 'update:selectedId', selectedId: string): void;
}>();

/**
 * 発注取込設定を選択する
 * @param selectedId - 選択した発注取込設定ID
 */
const selectOrderExcelInfo = (selectedId: string): void => {
  emits('update:selectedId', selectedId);
};

/**
 * 発注取込設定の行設定数表示を取得する
 * @param orderExcelInfo - 発注取込設定
 */
const getRowMappingRuleCountLabel = (orderExcelInfo: RegisterOrderExcelInfo): string => {
  return `${orderExcelInfo.rowMappingRules.length}${t('件')}`;
};
</script>

<template>
  <div class="order-excel-info-select-modal">
    <p class="order-excel-info-select-modal__description">
      {{ t('この発注ファイルの取り込みに使用する設定を選択してください。') }}
    </p>
    <div class="order-excel-info-select-modal__list" role="radiogroup" :aria-label="t('発注取込設定')">
      <label
        v-for="orderExcelInfo in props.orderExcelInfos"
        :key="orderExcelInfo.id"
        class="order-excel-info-select-modal__item"
        :class="{ 'order-excel-info-select-modal__item--selected': orderExcelInfo.id === props.selectedId }"
      >
        <input
          class="order-excel-info-select-modal__radio"
          type="radio"
          name="order-excel-info"
          :value="orderExcelInfo.id"
          :checked="orderExcelInfo.id === props.selectedId"
          @change="selectOrderExcelInfo(orderExcelInfo.id)"
        />
        <span class="order-excel-info-select-modal__item-content">
          <span class="order-excel-info-select-modal__name">{{ orderExcelInfo.name }}</span>
          <span class="order-excel-info-select-modal__meta">
            <span>{{ t('ヘッダー行') }}: {{ orderExcelInfo.headerLineNumber }}</span>
            <span>{{ t('行設定') }}: {{ getRowMappingRuleCountLabel(orderExcelInfo) }}</span>
          </span>
        </span>
      </label>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.order-excel-info-select-modal {
  display: grid;
  gap: 12px;
  min-width: 0;

  &__description {
    margin: 0;
    color: $textDefault;
    font-family: Noto Sans JP;
    font-size: 14px;
    line-height: 20px;
  }

  &__list {
    display: grid;
    gap: 8px;
    max-height: 360px;
    overflow-y: auto;
  }

  &__item {
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr);
    gap: 10px;
    align-items: start;
    padding: 10px 12px;
    border: 1px solid $borderDefault;
    border-radius: 6px;
    background-color: $semanticWhite;
    cursor: pointer;
  }

  &__item--selected {
    border-color: $semanticPrimary;
    background-color: $backgroundDefault;
  }

  &__radio {
    margin-top: 3px;
  }

  &__item-content {
    display: grid;
    gap: 4px;
    min-width: 0;
  }

  &__name {
    overflow: hidden;
    color: $textDefault;
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    color: $textSecondary;
    font-family: Noto Sans JP;
    font-size: 12px;
    line-height: 16px;
  }
}
</style>
